import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import type { Conversation, PresenceStatus, UserBrief } from "@/messaging/types";
import { EmptyState } from "./EmptyState";
import { CheckIcon, SearchIcon, UsersIcon } from "./icons";

export function NewChat({
  onCreated,
  presenceOf,
}: {
  onCreated: (c: Conversation) => void;
  presenceOf: (u: UserBrief | null | undefined) => PresenceStatus;
}) {
  const [teammates, setTeammates] = useState<UserBrief[] | null>(null);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState(false);
  const [selected, setSelected] = useState<Record<string, UserBrief>>({});
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<UserBrief[]>("/api/messaging/teammates")
      .then(setTeammates)
      .catch(() => setTeammates([]));
  }, []);

  const filtered = useMemo(() => {
    const list = teammates ?? [];
    const t = q.trim().toLowerCase();
    if (!t) return list;
    return list.filter((u) =>
      (u.full_name || u.email).toLowerCase().includes(t),
    );
  }, [teammates, q]);

  const selCount = Object.keys(selected).length;

  const startDirect = async (u: UserBrief) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api<Conversation>("/api/conversations", {
        method: "POST",
        json: { kind: "direct", other_user_id: u.id },
      });
      onCreated(c);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't start chat");
    } finally {
      setBusy(false);
    }
  };

  const createGroup = async () => {
    if (busy || selCount < 1 || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api<Conversation>("/api/conversations", {
        method: "POST",
        json: {
          kind: "group",
          title: title.trim(),
          member_ids: Object.keys(selected),
        },
      });
      onCreated(c);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't create group");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (u: UserBrief) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[u.id]) delete next[u.id];
      else next[u.id] = u;
      return next;
    });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* mode toggle */}
      <div className="p-2.5 pb-1.5">
        <div className="inline-flex w-full rounded-lg border border-white/10 bg-white/[0.03] p-0.5 text-xs">
          <button
            onClick={() => setGroup(false)}
            className={`flex-1 rounded px-3 py-1.5 font-medium transition ${
              !group ? "bg-accent-500/15 text-accent-200" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Direct
          </button>
          <button
            onClick={() => setGroup(true)}
            className={`flex-1 rounded px-3 py-1.5 font-medium transition inline-flex items-center justify-center gap-1.5 ${
              group ? "bg-accent-500/15 text-accent-200" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <UsersIcon size={14} /> Group
          </button>
        </div>
      </div>

      {group && (
        <div className="px-2.5 pb-1.5">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Group name"
            maxLength={120}
            className="input w-full !py-2 !rounded-lg"
          />
        </div>
      )}

      <div className="px-2.5 pb-1.5">
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <SearchIcon size={16} />
          </span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search teammates"
            className="input w-full !pl-9 !py-2 !rounded-lg"
          />
        </div>
      </div>

      {error && <div className="px-3 pb-1 text-[11px] text-rose-400">{error}</div>}

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        {teammates === null && (
          <div className="px-3 py-3 text-xs text-slate-500">Loading…</div>
        )}
        {teammates !== null && filtered.length === 0 && (
          <EmptyState title="No teammates found" />
        )}
        {filtered.map((u) => {
          const picked = !!selected[u.id];
          return (
            <button
              key={u.id}
              onClick={() => (group ? toggle(u) : startDirect(u))}
              disabled={busy}
              className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition ${
                picked ? "bg-accent-500/10" : "hover:bg-white/[0.03]"
              } disabled:opacity-60`}
            >
              <Avatar
                url={u.avatar_url}
                name={u.full_name}
                email={u.email}
                size="sm"
                status={presenceOf(u)}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-slate-200">
                  {u.full_name || u.email}
                </div>
                <div className="truncate text-[11px] text-slate-500">{u.email}</div>
              </div>
              {group && (
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                    picked
                      ? "border-accent-500 bg-accent-500 text-white"
                      : "border-white/20 text-transparent"
                  }`}
                >
                  <CheckIcon size={13} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {group && (
        <div className="border-t border-white/5 p-2.5">
          <button
            onClick={createGroup}
            disabled={busy || selCount < 1 || !title.trim()}
            className="btn-primary w-full !py-2"
          >
            Create group{selCount > 0 ? ` · ${selCount}` : ""}
          </button>
        </div>
      )}
    </div>
  );
}
